import { useMemo, useRef, useState, type CSSProperties } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { isEqual, sortBy } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import { useVirtualizer } from "@tanstack/react-virtual";

interface Props {
  options: { label: string; value: string }[];
  values: string[];
  disabled?: boolean;
  isSearchable?: boolean;
  dropdownClassName?: string;
  showViewTagsButton?: boolean;
  onChange: (values: string[] | null) => void;
  hasTooltip?: boolean;
}

export const TagsSelector = ({
  options,
  values,
  disabled,
  isSearchable,
  dropdownClassName,
  showViewTagsButton,
  onChange,
  hasTooltip = false,
}: Props) => {
  const scrollElRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [selectedValues, setSelectedValues] = useState<string[]>(values);

  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    options,
    {
      searchKeys: ["label"],
    },
  );

  const changeMenuOpen = (value: boolean) => {
    if (value) {
      clearSearch();
      setSelectedValues(values);
    }

    setIsOpen(value);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: changeMenuOpen,
    transform: false,
    placement: "bottom-start",
    middleware: [flip(), shift()],
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        animationFrame: true,
      });
      return cleanup;
    },
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const sortedOptions = useMemo(
    () =>
      sortBy(
        [...filteredOptions],
        [(e) => (selectedValues.includes(e.value) ? 0 : 1), "label"],
      ),
    [filteredOptions, selectedValues],
  );

  const rowVirtualizer = useVirtualizer({
    count: sortedOptions.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  return (
    <div className="shrink-0">
      <button
        disabled={disabled}
        className={clsx(
          "h-6 button tertiary-button rounded flex items-center justify-center",
          {
            "w-6": !showViewTagsButton,
            "px-2 rounded-full bg-cosmic-4 hover:bg-cosmic-3":
              showViewTagsButton && !isOpen,
            "px-2 rounded-full bg-cosmic text-white":
              showViewTagsButton && isOpen,
            "bg-cosmic text-white": isOpen,
            "bg-cosmic-3 hover:bg-cosmic-2":
              !isOpen && !disabled && !showViewTagsButton,
            "bg-gray-3": !isOpen && disabled,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {showViewTagsButton ? (
          <div className="flex items-center gap-2">
            <i
              className={clsx("fa-regular fa-eye text-sm", {
                "text-white": isOpen,
                "text-cosmic": !isOpen && !disabled,
                "text-gray": !isOpen && disabled,
              })}
            />
            View tags
          </div>
        ) : (
          <i
            className={clsx("fa-regular fa-list-check text-sm", {
              "text-white": isOpen,
              "text-cosmic": !isOpen && !disabled,
              "text-gray": !isOpen && disabled,
            })}
          />
        )}
      </button>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              ref={refs.setFloating}
              style={floatingStyles}
              className={clsx(
                "absolute z-[1] bg-white rounded-lg elevation-1 text-sm mt-1",
                dropdownClassName,
              )}
              {...getFloatingProps()}
            >
              {isSearchable && (
                <div className="px-4 pt-3">
                  <div className="nb-input-container relative">
                    <input
                      className="nb-input pr-8"
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <i
                      className={clsx(
                        "fa-regular fa-sharp fa-magnifying-glass text-[0.9rem] text-primary",
                        "w-4 absolute right-[12px] top-[10px]",
                      )}
                    />
                  </div>
                </div>
              )}
              {sortedOptions.length === 0 ? (
                <div className="text-disabled px-4 mt-3 mb-3">
                  NO RESULTS FOUND
                </div>
              ) : (
                <div className="pt-2">
                  <div className="px-4 h-9 w-full flex items-center gap-2 text-xs">
                    <Checkbox
                      value={selectedValues.length === options.length}
                      onChange={() =>
                        setSelectedValues(
                          selectedValues.length === options.length
                            ? []
                            : options.map((e) => e.value),
                        )
                      }
                    />
                    <span
                      className="cursor-pointer space-x-0.5"
                      onClick={() =>
                        setSelectedValues(
                          selectedValues.length === options.length
                            ? []
                            : options.map((e) => e.value),
                        )
                      }
                    >
                      <span>Select All</span>
                      {isSearchable && <span>({selectedValues.length})</span>}
                    </span>
                  </div>
                  <div
                    ref={scrollElRef}
                    className="mb-2 max-h-[170px] overflow-y-auto floatingui-scroll"
                  >
                    <div
                      style={{
                        position: "relative",
                        height: `${rowVirtualizer.getTotalSize()}px`,
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((e) => {
                        const row = sortedOptions[e.index];

                        return (
                          <Option
                            key={e.key}
                            hasTooltip={hasTooltip}
                            className="absolute top-0 left-0"
                            style={{
                              transform: `translateY(${e.start}px)`,
                            }}
                            value={selectedValues.includes(row.value)}
                            label={row.label}
                            onChange={(value) =>
                              setSelectedValues(
                                value
                                  ? [...selectedValues, row.value]
                                  : selectedValues.filter(
                                      (e) => e !== row.value,
                                    ),
                              )
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              <div className="border-t border-gray-2 py-2 px-2 flex justify-around gap-2">
                <TertiaryButton
                  className="font-bold"
                  disabled={selectedValues.length === 0 && values.length === 0}
                  onClick={() => {
                    onChange(null);
                    changeMenuOpen(false);
                  }}
                >
                  RESET
                </TertiaryButton>
                <PrimaryButton
                  className={clsx("font-bold")}
                  disabled={isEqual(selectedValues, values)}
                  onClick={() => {
                    onChange(selectedValues);
                    changeMenuOpen(false);
                  }}
                >
                  APPLY
                </PrimaryButton>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};

interface OptionProps {
  style?: CSSProperties;
  className?: string;
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  hasTooltip?: boolean;
}

export const Option = ({
  style,
  className,
  label,
  value,
  onChange,
  hasTooltip = false,
}: OptionProps) => {
  return (
    <div
      className={clsx(
        "px-4 h-9 w-full flex items-center gap-2 text-xs whitespace-nowrap",
        className,
      )}
      style={style}
    >
      <Checkbox value={value} onChange={() => onChange(!value)} />
      <div
        className={clsx(
          "text-cosmic bg-cosmic-3 h-7 px-3 rounded rounded-full flex items-center",
          "cursor-pointer hover:bg-cosmic-2",
        )}
        onClick={() => onChange(!value)}
      >
        <span className="truncate max-w-[200px]">{label}</span>
        {hasTooltip && <GenericTooltip content={label}></GenericTooltip>}
      </div>
    </div>
  );
};
